/* eslint-disable react/jsx-no-duplicate-props */
/*
=========================================================
* Material Kit 2 React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

// Formik
import { Formik, Form } from 'formik';
import * as yup from 'yup';

// Material Kit 2 React components
import MKBox from "../../../components/MaterialKit/MKBox";
import MKTypography from "../../../components/MaterialKit/MKTypography";
import MKInput from "../../../components/MaterialKit/MKInput";
import MKButton from "../../../components/MaterialKit/MKButton";

import SuccessModal from "../../../components/Modals/SuccessModal";
import FailModal from "../../../components/Modals/FailModal";

import {postJoinCollectors} from '../../../apis/comms';

const phoneRegExp = /^\s*(?:\+?(\d{1,3}))?([-. (]*(\d{3})[-. )]*)?((\d{3})[-. ]*(\d{2,4})(?:[-.x ]*(\d+))?)\s*$/gm;

const validationSchema = yup.object({
  name: yup.string()
      .required('Name is required'),
  mobile: yup.string().required('Mobile is required').matches(phoneRegExp, 'A valid mobile number needs to be captured'),
  email: yup.string().required('Email is required').email('Must be a valid email').max(255)
      .when(['mobile', 'landLine'], {                                                    
        is: (mobile, landLine) => ((!mobile || mobile.length === 0) && (!landLine || landLine.length ===0)),
        then: yup.string()                                                   
        .required('At least one contact detail is required'),                 
                    })
});

function Step1(props) {
  const [show, setShow] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isMobile, setIsMobile] = useState(props.isMobile);
  const [sessionInfo, setSessionInfo] = useState(props.sessionInfo);
  const [backdropClose, setBackdropClose] = useState(false);

  useEffect(() => {
    let cancel = false;
    if(cancel)
      return;
      
    setIsMobile(props.isMobile);
    setSessionInfo(props.sessionInfo);

    return () => { 
      cancel = true;
    }
  }, [props.isMobile, props.sessionInfo]);

  return (
    <>
      <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={backdropClose}
        >
        <CircularProgress color="inherit" />
      </Backdrop>
      <MKBox component="section" py={{ xs: 0, lg: 6 }}>
      <Container>
        <Grid container item>
          <MKBox
            width="100%"
            bgColor="white"
            borderRadius="xl"
            shadow="xl"
            mb={6}
            sx={{ overflow: "hidden" }}
          >
            <Grid container spacing={2}>
              <Grid
                item
                xs={12}
                lg={5}
                position="relative"
                px={0}
                sx={{
                  backgroundImage: ({
                    palette: { gradients },
                    functions: { rgba, linearGradient },
                  }) =>
                    `${linearGradient(
                      rgba(gradients.dark.main, 0.8),
                      rgba(gradients.dark.state, 0.8)
                    )}, url(${process.env.REACT_APP_CDN_Assets_Base}/pages/the-collectors/join/background.jpg)`,
                  backgroundSize: "cover",
                }}
              >
                <MKBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="100%"
                  height="100%"
                >
                  <MKBox py={6} pr={6} pl={{ xs: 6, sm: 12 }} my="auto">
                    <MKTypography variant="h3" color="white" mb={1}>
                      Join The Collectors
                    </MKTypography>
                    <MKTypography variant="body2" color="white" opacity={0.8} mb={3}>
                      We at Overdue are always looking for new potential partners to assist with Debt Collection. If you would like to submit your information as a company or Individual, please complete the form below and send it to us.
                    </MKTypography>
                  </MKBox>
                </MKBox>
              </Grid>
              <Grid item xs={12} lg={7}>
                <MKBox component="section" p={2} method="post">
                  <MKBox px={3} py={{ xs: 2, sm: 6 }}>
                    <MKTypography variant="h2" mb={1}>
                      Join Today!
                    </MKTypography>
                    <MKTypography variant="body1" color="text" mb={2}>
                      We look forward to hearing from you!
                    </MKTypography>
                  </MKBox>
                  <MKBox pt={0.5} pb={3} px={3}>
                    <Formik
                      enableReinitialize={true}
                      initialValues={{
                        name: sessionInfo && sessionInfo.given_name ? sessionInfo.given_name : '',
                        surname: sessionInfo && sessionInfo.family_name ? sessionInfo.family_name : '',
                        mobile: sessionInfo && sessionInfo.phone_number ? sessionInfo.phone_number : '',
                        email: sessionInfo && sessionInfo.email ? sessionInfo.email : '',
                        businessType: '',
                        companyName: ''
                    }}
                    validationSchema={validationSchema}
                    onSubmit={values => {
                      setBackdropClose(true);
                      setShow(false);
                      setShowError(false);
                      postJoinCollectors(values)
                        .then((data) => {
                          setBackdropClose(false);
                          setShow(true);
                        })
                        .catch((err) => {
                          setBackdropClose(false);
                          setShowError(true);
                        });
                    }}
                  >
                  {(props) => (
                    <Form>
                      <Grid container spacing={1} alignItems="center" sx={{ textAlign: "center" }}>
                        <Grid item xs={12}>
                          <MKInput 
                            type="text" 
                            label="Company Name"
                            id="companyName" 
                            value={props.values.companyName}
                            onChange={props.handleChange('companyName')}
                            error={props.errors.companyName && Boolean(props.errors.companyName)}
                            fullWidth 
                          />
                        </Grid>
                        <Grid item xs={12}>
                            <MKInput 
                              type="text" 
                              label="Name" 
                              id="name" 
                              value={props.values.name}
                              onChange={props.handleChange('name')}
                              error={props.errors.name && Boolean(props.errors.name)}
                              fullWidth 
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <MKInput 
                              type="text" 
                              label="Surname" 
                              id="surname" 
                              value={props.values.surname}
                              onChange={props.handleChange('surname')}
                              error={props.errors.surname && Boolean(props.errors.surname)}
                              fullWidth 
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <MKInput 
                              type="text" 
                              label="Mobile" 
                              id="mobile" 
                              value={props.values.mobile}
                              onChange={props.handleChange('mobile')}
                              error={props.errors.mobile && Boolean(props.errors.mobile)}
                              fullWidth 
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <MKInput 
                              type="email" 
                              label="Email" 
                              id="email" 
                              value={props.values.email}
                              onChange={props.handleChange('email')}
                              error={props.errors.email && Boolean(props.errors.email)}
                              fullWidth 
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <MKTypography variant="caption" color="error" align="center">
                            {props.errors.businessType && props.errors.businessType}
                            {!props.errors.businessType && props.errors.companyName && props.errors.companyName}
                            {!props.errors.businessType && !props.errors.companyName && props.errors.name && props.errors.name}
                            {!props.errors.businessType && !props.errors.companyName && !props.errors.name && props.errors.mobile && props.errors.mobile}
                            {!props.errors.businessType && !props.errors.companyName && !props.errors.name && !props.errors.mobile && !props.errors.landLine && props.errors.email && props.errors.email}
                            </MKTypography>
                          </Grid>
                          <Grid item xs={12} sx={{ textAlign: "right" }}>
                            <MKButton type="submit" variant="gradient" color="info" sx={{ height: "100%" }}>
                              Submit
                            </MKButton>
                          </Grid>
                        </Grid>
                      </Form>
                      )}
                  </Formik>
                  </MKBox>
                </MKBox>
              </Grid>
            </Grid>
          </MKBox>
        </Grid>
      </Container>
    </MKBox>
    <SuccessModal isMobile={isMobile} show={show} header="Message sent" text="Thank you for getting in touch. You can expect a response from us." />
    <FailModal isMobile={isMobile} show={showError} header="Message not sent" text="We appreciate you getting in touch, unfortunately we didnt receive your message." />
</>
  );
}

export default Step1;